// `hide` — hiding the shop's own elements that a widget replaces (the native autocomplete list,
// native cross-sell blocks, a previous vendor's recommendations), as a facade-owned option on
// each search / rce activation wrapper (../config.ts):
//
//   hide: "#native-autocomplete"                               // every width
//   hide: [".old-reco", { selector: ".native-xsell", below: 768 }]
//
// WHEN (iteration 21 — "hide at boot"): the booted config's search widgets (none on an
// experiment's null arm) whose page activation is already known at widget.js execution get
// their rules into ONE facade `<style data-rvn-hide="…">` appended to <head> right then, before the DOM wait
// — so the parser hasn't even reached the native elements yet and they never paint (and can't be
// clicked / typed into) before our widget exists:
//   - search dropdown / modal: always (they have no page predicate);
//   - search results: when it has no `activateWhen`, or a SYNC predicate that returned true at
//     script start (products/search.ts prepareSearch(); also when a predicate that said false
//     while the document was loading says true at its one re-check at DOM readiness — then
//     injected at that moment, the same way).
// Every other widget keeps the iteration 17 route, THROUGH `customPageStyles`: the compiled rules
// are appended to the widget config's `customPageStyles` — the page stylesheet the SDK itself
// injects into <head> — by the product init code (products/search.ts, products/rce.ts), so they
// inherit the SDK's timing exactly:
//   - search results with an async predicate / a trigger: injected when the widget is created
//     (its container is mounted);
//   - search dropdown / modal, only if the boot <style> could not be injected: when the widget is
//     first OPENED (its build() runs in open()), once per widget type;
//   - rce carousels (always — deliberate, iteration 17): injected only once a draw HAS PRODUCTS,
//     deduplicated by content — so a native block stays visible while our carousel has come back
//     empty (or failed).
// A widget's rules go one way or the other, never both. Either <style> is never removed: from
// that moment the elements stay hidden for the rest of the page view — closing the dropdown does
// not bring the native list back, a later EMPTY redraw of a carousel drawn on every event
// (`once: false`) leaves the native block hidden, and (accepted trade-off, iteration 21) a widget
// whose create() fails after its rules were injected at boot leaves its native element hidden.
// No widget → nothing hidden: an experiment's null arm (or any arm without that widget)
// keeps the shop's own elements. That is why hiding never goes into the light stylesheet
// (widget.css) unscoped: it loads on every arm (a customer rule there must sit under
// `[data-rvn-active]`, which boot() sets on <html> only when the booted config has widgets —
// for host elements every widget-bearing arm replaces — or under `[data-rvn-ab="<n>"]` for one
// arm — see the head of ../boot.ts and ../ab.ts).
//
// Pure and import-free (Node type stripping loads it under `pnpm test`, test/hide.test.ts).

// One element (or selector list) to hide. A plain string hides it at every width; the object
// form limits it to a viewport range: `below: N` → narrower than N px, `above: N` → at least
// N px wide, both → `above` ≤ width < `below`. `below: N` and `above: N` complement each other
// (see compileHideRule for the exact media queries).
export type HideRule = string | { selector: string; below?: number; above?: number };

// One rule, or several (compiled in order, one CSS rule each).
export type HideSpec = HideRule | readonly HideRule[];

// `below: N` ends 0.02px under N — the min-width / max-width pair Bootstrap uses (and this repo's
// SCSS, e.g. `max-width: 639.98px`): no overlap with `above: N` at N, and the 0.02px between them
// is reachable only at fractional (zoomed) viewport widths. Range syntax (`width < N`) would be
// exact but needs Safari 16.4 / Chrome 104, and this string is injected verbatim at runtime (no
// build-time transform, no autoprefixer), so the widely supported form is used.
const BELOW_OFFSET_PX = 0.02;

const DECLARATION = "{ display: none !important; }";

// The CSS for a whole `hide` spec: one `<selector> { display: none !important; }` rule per
// entry, wrapped in `@media` when bounded, newline-separated, in order. `undefined` / `[]` → "".
// Throws (naming the offending entry) on an invalid rule — see compileHideRule.
export function compileHide(spec: HideSpec | undefined): string {
  if (spec === undefined) return "";
  if (!Array.isArray(spec)) return compileHideRule(spec);
  return spec.map((rule: unknown, index: number) => compileHideRule(rule, `hide[${index}]`)).join("\n");
}

// One rule:
//   "<sel>"                         → <sel> { display: none !important; }
//   { selector, above: A }          → @media (min-width: Apx) { … }
//   { selector, below: B }          → @media (max-width: (B-0.02)px) { … }
//   { selector, above: A, below: B} → @media (min-width: Apx) and (max-width: (B-0.02)px) { … }
// Validated, since `hide` is customer config and a malformed selector could break the rules
// AFTER it in the same <style> (the widget's own customPageStyles included) — an unclosed
// bracket, string or comment swallows the following text, and in the one-line `@media … { … }`
// form even leaves the media block open around later rules:
//   - the selector must be a non-empty string that is structurally closed (checkSelectorSyntax):
//     no `{` / `}`, no comment (`/*`), balanced `[]` / `()`, closed quotes, no trailing `\`;
//   - `below` / `above`, when present, must be finite numbers ≥ 1 (px);
//   - with both, `below` must be greater than `above` (else the range is empty).
// That makes a rule self-contained, NOT valid: the selector is otherwise not parsed (no DOM, no
// CSS.supports here). An invalid or unsupported selector makes the browser drop that one rule —
// the WHOLE rule, so in a selector list (`a, b`) one part a browser doesn't support (e.g.
// `:has()` in an older one) hides none of them: give selectors of differing support separate
// entries (the array form) instead of one list. `label` names the entry in the error message.
export function compileHideRule(rule: unknown, label = "hide"): string {
  const { selector, below, above } =
    typeof rule === "string" ? { selector: rule, below: undefined, above: undefined } : asObject(rule, label);
  if (typeof selector !== "string" || selector.trim() === "") {
    throw new Error(`[@raventic/sdk] ${label}: the selector must be a non-empty string`);
  }
  checkSelectorSyntax(selector, label);
  checkWidth(below, "below", label);
  checkWidth(above, "above", label);
  if (below !== undefined && above !== undefined && below <= above) {
    throw new Error(
      `[@raventic/sdk] ${label}: "below" (${below}) must be greater than "above" (${above}) — the range is empty`,
    );
  }

  const css = `${selector.trim()} ${DECLARATION}`;
  const conditions: string[] = [];
  if (above !== undefined) conditions.push(`(min-width: ${px(above)})`);
  if (below !== undefined) conditions.push(`(max-width: ${px(below - BELOW_OFFSET_PX)})`);
  return conditions.length === 0 ? css : `@media ${conditions.join(" and ")} { ${css} }`;
}

// `config` with the compiled `hide` appended to its `customPageStyles` (after any existing value,
// newline-separated). Never mutates `config`: a shallow copy when there is something to add,
// `config` itself when `hide` compiles to nothing. Throws like compileHide.
export function withHide<C extends { customPageStyles?: string }>(config: C, hide: HideSpec | undefined): C {
  return withPageStyles(config, compileHide(hide));
}

// withHide() for already compiled rules (`css`, "" = nothing to add).
export function withPageStyles<C extends { customPageStyles?: string }>(config: C, css: string): C {
  if (css === "") return config;
  const existing = config.customPageStyles;
  return { ...config, customPageStyles: existing ? `${existing}\n${css}` : css };
}

// The attribute marking the facade's own hide <style> (value: the widgets whose rules it holds,
// space-separated, e.g. "dropdown results") — for tests and for debugging on a live page.
export const HIDE_STYLE_ATTRIBUTE = "data-rvn-hide";

// Appends `<style data-rvn-hide="<widgets>">css</style>` to <head> (iteration 21, see the
// header); `nonce` = the widgets' `cspNonce`, when one is set. Nothing for an empty `css`.
// Idempotent: an existing facade hide <style> with the same widgets and rules (a dev-harness
// re-run, a second boot()) is returned instead of appending a duplicate. Returns the element
// (null when nothing was appended). Throws on a DOM failure — the caller then falls back to
// `customPageStyles`.
export function injectHideStyle(
  css: string,
  widgets: readonly string[],
  nonce?: string,
  doc: Document = document,
): HTMLStyleElement | null {
  if (css === "") return null;
  const label = widgets.join(" ");
  for (const existing of Array.from(doc.querySelectorAll<HTMLStyleElement>(`style[${HIDE_STYLE_ATTRIBUTE}]`))) {
    if (existing.getAttribute(HIDE_STYLE_ATTRIBUTE) === label && existing.textContent === css) return existing;
  }
  const style = doc.createElement("style");
  style.setAttribute(HIDE_STYLE_ATTRIBUTE, label);
  if (nonce) style.nonce = nonce;
  style.textContent = css;
  (doc.head ?? doc.getElementsByTagName("head")[0]).appendChild(style);
  return style;
}

function asObject(rule: unknown, label: string): { selector: unknown; below: unknown; above: unknown } {
  if (rule === null || typeof rule !== "object") {
    throw new Error(`[@raventic/sdk] ${label}: expected a selector string or { selector, below?, above? }`);
  }
  const { selector, below, above } = rule as Record<string, unknown>;
  return { selector, below, above };
}

function checkWidth(value: unknown, key: "below" | "above", label: string): asserts value is number | undefined {
  if (value === undefined) return;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 1) {
    throw new Error(`[@raventic/sdk] ${label}: "${key}" must be a finite number of px ≥ 1 (got ${String(value)})`);
  }
}

// Rejects a selector that would not end where it is spliced in, i.e. could swallow or nest the
// rules after it. A small CSS-tokenizer subset: a backslash escapes the next character (anywhere);
// inside a '…' / "…" string only the matching quote ends it (brackets, `/*`, braces are plain
// text there; a newline makes it a CSS bad-string, so it is rejected); outside strings `{` / `}`
// and `/*` are rejected and `[` `(` must close in order. Not a selector validator — see
// compileHideRule.
export function checkSelectorSyntax(selector: string, label = "hide"): void {
  const fail = (problem: string): never => {
    throw new Error(`[@raventic/sdk] ${label}: the selector ${problem} (${JSON.stringify(selector)})`);
  };
  const closers: string[] = [];
  let quote: string | null = null;
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i]!;
    if (ch === "\\") {
      if (i === selector.length - 1) fail("must not end with a backslash");
      i++; // the escaped character, whatever it is
      continue;
    }
    if (quote !== null) {
      if (ch === quote) quote = null;
      else if (/[\n\r\f]/.test(ch)) fail("has a line break inside a quoted string");
      continue;
    }
    if (ch === '"' || ch === "'") quote = ch;
    else if (ch === "{" || ch === "}") fail('must not contain "{" or "}"');
    else if (ch === "/" && selector[i + 1] === "*") fail('must not contain a comment ("/*")');
    else if (ch === "[") closers.push("]");
    else if (ch === "(") closers.push(")");
    else if (ch === "]" || ch === ")") {
      if (closers.pop() !== ch) fail(`has an unbalanced "${ch}"`);
    }
  }
  if (quote !== null) fail(`has an unclosed ${quote} string`);
  if (closers.length > 0) fail(`has an unclosed "${closers[closers.length - 1] === "]" ? "[" : "("}"`);
}

// A px length without float noise (768 - 0.02 → "767.98px", never "767.9799999999999px").
function px(value: number): string {
  return `${Number(value.toFixed(4))}px`;
}
